import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '../authentication.service';
import { Observable, Subject, filter } from 'rxjs';
import { SignalEvent, SignalEventType, WareHouseLogSourceEnum } from './SignalModel';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection!: signalR.HubConnection;
  private signalEvent!: Subject<any>;

  constructor(
    private authService: AuthenticationService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.signalEvent = new Subject<any>();
  }

  public startConnection = () => {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.signalR, {
        accessTokenFactory: () => this.authService.currentUserValue!.token,
        transport: signalR.HttpTransportType.WebSockets
      }).build();

    this.hubConnection.start()
      .then((data) => {
        this.setupSignalREvents();
        this.authService.SignalRConnectionId.next(this.hubConnection.connectionId);
        // localStorage.setItem('connectionId', String(this.hubConnection.connectionId))
        console.log('Connection started ...')
      })
      .catch((err: any) => console.log('Error while starting connection ... ' + err));

  }
  stopConnection() {
    if (this.hubConnection) {
      this.hubConnection.stop()
        .then(() => console.log('Connection stopped ...'))
        .catch((err: any) => console.log('Error while stopping connection ... ' + err));
    }
  }
  /*
   * ***************************************************************
   * ***************************************************************
   * ***************************************************************
   * ***************************************************************
  */

  getDataStream<TDataShape>(...filterValues: SignalEventType[]): Observable<SignalEvent<TDataShape>> {
    return this.signalEvent.asObservable().pipe(filter(event => filterValues.some(f => f === event.type)));
  }

  private onMessage<TDataShape>(payload: SignalEvent<TDataShape>) {
    this.signalEvent.next(payload);
  }

  private setupSignalREvents() {
    this.hubConnection?.on('LogOut', (msgEn: string, msgAr: string) => {
      this.onMessage({ type: SignalEventType.LogOut, data: { msgEn, msgAr } });
    });
    this.hubConnection?.on('MobNotificationUnreadCountNumber', (count: number) => {
      this.onMessage({ type: SignalEventType.MobNotificationUnreadCountNumber, data: { count } });
    });
    this.hubConnection?.on('NewReservation', (RestaurantId: number, TabelsIds: Number[]) => {
      this.onMessage({ type: SignalEventType.NewReservation, data: { RestaurantId, TabelsIds } });
    });
    this.hubConnection?.on('UpdateReservation', (RestaurantId: number, ReservationId: Number) => {
      this.onMessage({ type: SignalEventType.UpdateReservation, data: { RestaurantId, ReservationId } });
    });
    this.hubConnection?.on('CancelReservation', (ReservationId: number, RestaurantId: Number) => {
      this.onMessage({ type: SignalEventType.CancelReservation, data: { ReservationId, RestaurantId } });
    });
    this.hubConnection?.on('menuUpdated', (RestaurantId: number) => {
      this.onMessage({ type: SignalEventType.menuUpdated, data: { RestaurantId } });
    });
    this.hubConnection?.on('menuItemUpdated', (RestaurantId: number, ItemId: number) => {
      this.onMessage({ type: SignalEventType.menuItemUpdated, data: { RestaurantId, ItemId } });
    });
    this.hubConnection?.on('kdsItemStatusChanged', (RestaurantId: number, ReservationId: number, OrderId: number, ItemId: number, NewStatus: string) => {
      this.onMessage({ type: SignalEventType.kdsItemStatusChanged, data: { RestaurantId, ReservationId, OrderId, ItemId, NewStatus } });
    });
    this.hubConnection?.on('orderStatusChanged', (RestaurantId: number, ReservationId: number, OrderId: number) => {
      this.onMessage({ type: SignalEventType.orderStatusChanged, data: { RestaurantId, ReservationId, OrderId } });
    });
    this.hubConnection?.on('TakeawayorderStatusChanged', (RestaurantId: number, OrderId: number, NewStatus: string) => {
      this.onMessage({ type: SignalEventType.TakeawayorderStatusChanged, data: { RestaurantId, OrderId, NewStatus } });
    });
    this.hubConnection?.on('QrCodeHyperPay', (RestaurantId: number, CheckoutId: string, OrderId: number, Type: string) => {
      this.onMessage({ type: SignalEventType.QrCodeHyperPay, data: { RestaurantId, CheckoutId, OrderId, Type } });
    });
    this.hubConnection?.on('NearPayNFC', (RestaurantId: number, OrderId: number, Type: string, canPrint: string, CheckoutId: string) => {
      this.onMessage({ type: SignalEventType.NearPayNFC, data: { RestaurantId, OrderId, Type, canPrint, CheckoutId } });
    });
    this.hubConnection?.on('newOrderToKds', (RestaurantId: number, ReservationId: number, OrderId: number) => {
      this.onMessage({ type: SignalEventType.newOrderToKds, data: { RestaurantId, ReservationId, OrderId } });
    });
    this.hubConnection?.on('editOrder', (RestaurantId: number, ReservationId: number, OrderId: number) => {
      this.onMessage({ type: SignalEventType.editOrder, data: { RestaurantId, ReservationId, OrderId } });
    });
    this.hubConnection?.on('OrderCheckOut', (data: string) => {
      data = JSON.parse(data);
      this.onMessage({ type: SignalEventType.OrderCheckOut, data });
    });
    this.hubConnection?.on('cancelledOrder', (RestaurantId: number, ReservationId: number, OrderId: number) => {
      this.onMessage({ type: SignalEventType.cancelledOrder, data: { RestaurantId, ReservationId, OrderId } });
    });
    this.hubConnection?.on('recallOrder', (RestaurantId: number, ReservationId: number, OrderId: number) => {
      this.onMessage({ type: SignalEventType.recallOrder, data: { RestaurantId, ReservationId, OrderId } });
    });
    this.hubConnection?.on('wasteOrder', (RestaurantId: number, ReservationId: number, OrderId: number) => {
      this.onMessage({ type: SignalEventType.wasteOrder, data: { RestaurantId, ReservationId, OrderId } });
    });
    this.hubConnection?.on('updatePaymentSettings', (RestaurantId: number) => {
      this.onMessage({ type: SignalEventType.updatePaymentSettings, data: { RestaurantId } });
    });
    this.hubConnection?.on('blockTable', (RestaurantId: number, TableId: number) => {
      this.onMessage({ type: SignalEventType.blockTable, data: { RestaurantId, TableId } });
    });
    this.hubConnection?.on('WareHouseSenderReject', (RestaurantId: number, WarehouseId: number, RequestId: number, Type: WareHouseLogSourceEnum) => {
      this.onMessage({ type: SignalEventType.WareHouseSenderReject, data: { RestaurantId, WarehouseId, RequestId, Type } });
    });
    this.hubConnection?.on('WareHouseSenderAccept', (RestaurantId: number, WarehouseId: number, RequestId: number, Type: WareHouseLogSourceEnum) => {
      this.onMessage({ type: SignalEventType.WareHouseSenderAccept, data: { RestaurantId, WarehouseId, RequestId, Type } });
    });
    this.hubConnection?.on('WareHouseReceiver', (RestaurantId: number, WarehouseId: number, RequestId: number, Type: WareHouseLogSourceEnum) => {
      this.onMessage({ type: SignalEventType.WareHouseReceiver, data: { RestaurantId, WarehouseId, RequestId, Type } });
    });
    this.hubConnection?.on('AttandanceFeature', (RestaurantId) => {
      this.onMessage({ type: SignalEventType.AttandanceFeature, data: { RestaurantId } });
    });
  }

  /*******************************************************/
  notify(msg: string) {
    this.snackBar.open(this.translate.instant('signal.' + msg), '✖', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }
}
